/* eslint-disable react/no-multi-comp */
import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { FileSearch, Infinity } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction } from 'shared/api';
import { TIME_ZONES } from 'shared/config';
import { translated } from 'shared/lib';
import { accountRoute } from 'shared/routing';
import { createDateFilter, createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import { TransactionRegistryModel } from '../../model';
import cls from './transaction-registry.module.scss';

export type TransactionRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (transaction: Transaction) => ReactNode;
};

export const createTransactionRegistry = ({
	model,
	currencies,
	dateFilterModel,
}: TransactionRegistryModel) => {
	const Registry = createRegistry(model);

	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			className: cls.transactionRegistryDateFilter,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'transaction-registry-time-zone-filter',
			label: translated('transaction-registry.filters.time-zone.label'),
			placeholder: translated('transaction-registry.filters.time-zone.placeholder'),
			className: cls.transactionRegistryTimeZoneFilter,
			classNames: { label: cls.transactionRegistryTimeZoneFilterLabel },
			value: model.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				model.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			id: 'transaction-registry-currencies-filter',
			label: translated('transaction-registry.filters.currencies.label'),
			placeholder: translated('transaction-registry.filters.currencies.placeholder'),
			className: cls.transactionRegistryCurrenciesFilter,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			data: currencies,
		},
	});

	const TransactionRegistry = forwardRef<HTMLDivElement, TransactionRegistryProps>(
		({ emptyActionSlot, topBarActionSlot, tableTdActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.TopBar alwaysShow>
						<Registry.Filters className={cls.transactionRegistryFilters}>
							<DateField data-test-id='transaction-registry-date-filter' />
							<TimeZoneField />
							<CurrenciesField />
						</Registry.Filters>
						<div className={cls.transactionRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.transactionRegistryColDate} />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.from')}
								</Registry.Table.Th>
								<Registry.Table.Th>{t('transaction-registry.table-columns.to')}</Registry.Table.Th>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.currency')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.amount')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.type')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.balanceAfter')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('transaction-registry.table-columns.date')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>
											{row.from && (
												<Anchor
													component={Link}
													to={accountRoute}
													data-test-id={`transaction-registry-account-link-${row.from.id}`}
													query={{ id: row.from.id }}
												>
													{row.from.name}
												</Anchor>
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.to && (
												<Anchor
													component={Link}
													to={accountRoute}
													data-test-id={`transaction-registry-account-link-${row.to.id}`}
													query={{ id: row.to.id }}
												>
													{row.to.name}
												</Anchor>
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td
											className={cls.transactionRegistryAmount}
											data-type={row.type}
										>
											<NumberFormatter value={row.amount} />
										</Registry.Table.Td>
										<Registry.Table.Td
											className={cls.transactionRegistryType}
											data-type={row.type}
										>
											{t(`transaction-registry.table-columns.${row.type}`)}
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.balanceAfter !== null ? (
												<NumberFormatter value={row.balanceAfter} />
											) : (
												<Infinity style={{ fontSize: 16 }} />
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.createdAt}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('transaction-registry.empty.title')}
						message={t('transaction-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						withClearFiltersButton={false}
						icon={<FileSearch />}
						title={t('transaction-registry.empty.title')}
						message={t('transaction-registry.empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return TransactionRegistry;
};
